import { useContext, useLayoutEffect, useRef, type CSSProperties, type ReactNode } from "react";
import { LogoWaits, playLogo } from "./clock";
import { MARK_WIDE, WORD_WIDE } from "./drawing";
import { LogoAnimation } from "./LogoAnimation";
import { besideTheMark } from "./timeline";

/** The room of the logo in the bar, in heights of the mark (.brand in till.css). */
const ROOM = { "--mark-wide": MARK_WIDE, "--word-wide": WORD_WIDE } as CSSProperties;

/**
 * The logo at the left of a bar (Furkan, 02/10/2026; ADR 0016): "TAFO" is written with the page and turns
 * into the mark, which stays at the very left. What the screen is called stands next to the mark. While
 * the word is there it runs over that place, so the name appears when the mark stands; the bar gives the
 * logo the width of the word (till.css), and nothing in it moves aside. `white`: in the colour of the text
 * around it, on the red bar of the till in the Tafo app.
 */
export function BarLogo({ children, white = false }: { children: ReactNode; white?: boolean }) {
  const brand = useRef<HTMLDivElement>(null);
  const waits = useContext(LogoWaits);

  // before the screen is drawn: the name is never seen and then taken away
  useLayoutEffect(() => {
    if (waits || brand.current === null) return;
    const animations = playLogo(brand.current, [besideTheMark()], "page");
    return () => {
      for (const animation of animations) animation.cancel();
    };
  }, [waits]);

  return (
    <div className="brand" ref={brand} style={ROOM}>
      <span className="brand-logo">
        <LogoAnimation layout="start" clock="page" white={white} />
      </span>
      <small data-part="beside">{children}</small>
    </div>
  );
}
